import uuid from "uuid";
import {WizardGroup, WizardItem} from "../../../../../interfaces/Wizard";

/**
 * Decide which groups are "valid" (visible/allowed) besides enabled/disabled.
 * NOTE: this is your existing logic, pulled into a function.
 */
function isGroupAllowed({ wizGroup, wizardItem, currentVisType }) {
  if (wizGroup.hideForCustomers && !document.kvConfig.isBackend) return false;

  if (wizardItem.visualizer === "all") {
    return (
      wizGroup.groupName === "Room" ||
      !Array.isArray(wizGroup.visibleInVisualizers) ||
      !wizGroup.visibleInVisualizers.length ||
      wizGroup.visibleInVisualizers.includes(currentVisType)
    );
  }

  return true;
}

function buildAdj(edges) {
  const out = new Map(); // source -> [edge]
  const inc = new Map(); // target -> [edge]
  for (const e of edges) {
    if (!out.has(e.source)) out.set(e.source, []);
    if (!inc.has(e.target)) inc.set(e.target, []);
    out.get(e.source).push(e);
    inc.get(e.target).push(e);
  }
  return { out, inc };
}

function edgeKey(source, target, sourceHandle = null, targetHandle = null) {
  return `${source}|${target}|${sourceHandle ?? ""}|${targetHandle ?? ""}`;
}

function addEdgeUnique(edges, seen, edge) {
  const k = edgeKey(edge.source, edge.target, edge.sourceHandle, edge.targetHandle);
  if (seen.has(k)) return;
  seen.add(k);
  edges.push(edge);
}

/**
 * Main graph transform:
 * - removes groups that are "not allowed" (hideForCustomers/visualizer rules)
 * - handles disabled groups with your sibling/bypass logic
 * - removes unreachable groups/edges (from startId)
 */
export function pruneWizardItemGraph(wizardItem: WizardItem, currentVisType) {
  const startId = "0";
  const groupsById = new Map<string, WizardGroup>(wizardItem.wizardGroups.map((g) => [String(g.id), g]));
  let edges = wizardItem.wizardEdges.map((e) => ({
    ...e,
    source: String(e.source),
    target: String(e.target),
  }));

  // 1) Hard-remove groups that are not allowed by non-enabled rules (eyeBall/visualizer etc.)
  const hardRemoved = new Set();
  for (const g of wizardItem.wizardGroups) {
    if (!isGroupAllowed({ wizGroup: g, wizardItem, currentVisType })) {
      hardRemoved.add(String(g.id));
    }
  }

  // Remove edges touching hard-removed nodes (no bypass here by default)
  edges = edges.filter((e) => !hardRemoved.has(e.source) && !hardRemoved.has(e.target));

  // 2) Disabled handling with "remove vs bypass" rule
  // We'll look at "valid children" of each parent (children that are NOT hardRemoved).
  // The sibling rule uses presence of another valid child besides the disabled node.
  const isDisabled = (id) => {
    const g = groupsById.get(id);
    return !!g && g.enabled === false;
  };

  const disabledIds = new Set<string>(
    wizardItem.wizardGroups
      .map((g) => String(g.id))
      .filter((id) => !hardRemoved.has(id) && isDisabled(id))
  );

  // We'll iteratively process because bypassing changes edges.
  // Chains of disabled nodes — and parents whose children are ALL disabled —
  // require multiple passes, so we loop until there's nothing left to do.
  //
  // The decision is made per-parent (not per-node), because whether a disabled
  // child should be bypassed or simply dropped depends on its siblings:
  //   - If the parent has at least one ENABLED child, every disabled child is
  //     just removed — the user can still reach the enabled sibling.
  //   - If ALL of the parent's children are disabled, we bypass through the
  //     FIRST disabled child (reconnecting the parent to that child's children)
  //     and drop the remaining disabled children.
  for (let guard = 0; guard < 50; guard++) {
    const { out } = buildAdj(edges);

    const edgesToRemove = new Set<string>(); // edge keys to drop
    const bypasses: Array<{ parentId: string; childId: string }> = [];

    for (const [parentId, childEdges] of out.entries()) {
      const disabledChildEdges = childEdges.filter(
        (e) => disabledIds.has(e.target) && !hardRemoved.has(e.target)
      );
      if (!disabledChildEdges.length) continue;

      const hasEnabledChild = childEdges.some(
        (e) => !disabledIds.has(e.target) && !hardRemoved.has(e.target)
      );

      if (hasEnabledChild) {
        // At least one enabled sibling exists → drop the disabled children.
        for (const e of disabledChildEdges) {
          edgesToRemove.add(edgeKey(e.source, e.target, e.sourceHandle, e.targetHandle));
        }
      } else {
        // Every child is disabled → bypass through the first, drop the rest.
        const [firstDisabled, ...rest] = disabledChildEdges;
        bypasses.push({ parentId, childId: firstDisabled.target });
        edgesToRemove.add(
          edgeKey(firstDisabled.source, firstDisabled.target, firstDisabled.sourceHandle, firstDisabled.targetHandle)
        );
        for (const e of rest) {
          edgesToRemove.add(edgeKey(e.source, e.target, e.sourceHandle, e.targetHandle));
        }
      }
    }

    // Nothing left to bypass or remove → we're done.
    if (!edgesToRemove.size && !bypasses.length) break;

    // Rebuild the edge list: keep everything we're not removing, then splice in
    // the bypass edges (parent -> each child of the bypassed disabled node).
    const nextEdges = [];
    const seen = new Set();

    for (const e of edges) {
      if (edgesToRemove.has(edgeKey(e.source, e.target, e.sourceHandle, e.targetHandle))) continue;
      addEdgeUnique(nextEdges, seen, e);
    }

    for (const { parentId, childId } of bypasses) {
      const parentEdge = (out.get(parentId) || []).find((e) => e.target === childId);
      const grandChildren = out.get(childId) || [];

      for (const gEdge of grandChildren) {
        // Avoid self loops
        if (gEdge.target === parentId) continue;

        addEdgeUnique(nextEdges, seen, {
          id: `reactflow__edge-${parentId}-${gEdge.target}-${uuid()}`,
          source: parentId,
          target: gEdge.target,
          sourceHandle: parentEdge?.sourceHandle ?? null,
          targetHandle: gEdge.targetHandle ?? null,
        });
      }
    }

    edges = nextEdges;
  }

  // 3) Reachability cleanup:
  // keep only nodes reachable from startId through edges
  const { out: outFinal } = buildAdj(edges);
  const reachable = new Set([String(startId)]);
  const queue = [String(startId)];

  while (queue.length) {
    const cur = queue.shift();
    const outs = outFinal.get(cur) || [];
    for (const e of outs) {
      if (!reachable.has(e.target)) {
        reachable.add(e.target);
        queue.push(e.target);
      }
    }
  }

  // Filter groups: must be reachable AND not hardRemoved AND enabled (because disabled should be gone now)
  const wizardGroups = wizardItem.wizardGroups.filter((g) => {
    const id = String(g.id);
    if (hardRemoved.has(id)) return false;
    if (g.enabled === false) return false;
    return reachable.has(id);
  });

  // Filter edges: both ends must be reachable; and targets/sources must not be hardRemoved
  const wizardEdges = edges.filter((e) => {
    if (hardRemoved.has(e.source) || hardRemoved.has(e.target)) return false;
    return reachable.has(e.source) && reachable.has(e.target);
  });

  return {
    ...wizardItem,
    wizardGroups,
    wizardEdges,
  };
}
